import React, { useEffect, useState, useRef, useMemo } from 'react'
import { Aggregation, NestedDict } from 'app/types'
import {
    LineChart,
    CartesianGrid,
    XAxis,
    YAxis,
    Line,
    ResponsiveContainer,
    Legend,
    Tooltip,
} from 'recharts'
import { IconButton, Button } from 'components'
import * as moment from 'moment'
import distinctColors from 'distinct-colors'
import { getAnalyticsColor, formatYAxisLabelWithWrap } from 'app/analytics'

// Enhanced date formatting types and utilities
type DateFormatType = 'daily' | 'monthly' | 'yearly' | 'custom'

interface DateFormatConfig {
    type: DateFormatType
    format?: string // moment.js format string for custom formatting
}

// Smart date granularity detection
const detectDateGranularity = (data: any[], dateField?: string): DateFormatType => {
    if (!data || data.length === 0) return 'daily'
    
    // Find the date field to analyze
    const fieldToCheck = dateField || 'date'
    
    const dates = data
        .map(item => item[fieldToCheck])
        .filter(dateStr => dateStr)
        .map(dateStr => new Date(dateStr))
        .filter(date => !isNaN(date.getTime()))
    
    if (dates.length === 0) return 'daily'
    
    // Check if all dates are the first day of the month (monthly data)
    const allFirstOfMonth = dates.every(date => date.getDate() === 1)
    if (allFirstOfMonth) return 'monthly'
    
    // Check if all dates are January 1st (yearly data)
    const allFirstOfYear = dates.every(date => date.getDate() === 1 && date.getMonth() === 0)
    if (allFirstOfYear) return 'yearly'
    
    // Default to daily
    return 'daily'
}

// Enhanced date formatter with multiple format options
const createDateFormatter = (config: DateFormatConfig) => {
    return (item: string | number | Date): string => {
        const date = item instanceof Date ? item : new Date(item)
        
        if (isNaN(date.getTime())) {
            return String(item) // Return original if not a valid date
        }
        
        switch (config.type) {
            case 'monthly':
                return moment(date).format('MMM YYYY')
            case 'yearly':
                return moment(date).format('YYYY')
            case 'custom':
                return moment(date).format(config.format || 'MMM DD')
            case 'daily':
            default:
                return moment(date).format('MMM DD')
        }
    }
}

const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const dayFormatter = (item: number) => days[item]
const hourFormatter = (item: number) => (item > 12 ? `${(item - 12)} pm` : `${item} am`)

// Format legend labels using the toReadable() string extension function
const formatLegendText = (value: string) => {
    if (typeof value !== 'string') return value
    return value.toReadable()
}

// Enhanced CustomLabel with resize handling capabilities and duration support
const CustomLabel = (props: any) => {
    const { x, y, value, isDuration, dataKey } = props

    // Don't render if position is invalid during resize
    if (x === undefined || y === undefined || isNaN(x) || isNaN(y)) {
        return null
    }

    // Format the value if it's a duration
    let displayValue = value
    if (isDuration && typeof value === 'number') {
        displayValue = formatDuration(value)
    }

    return (
        <g>
            <circle cx={x} cy={y} r={13} fill="white" stroke="#ccc" strokeWidth={1} />
            <text
                x={x}
                y={y}
                dy={5}
                textAnchor="middle"
                fill="#333"
                style={{ fontSize: '11px' }}
            >
                {displayValue}
            </text>
        </g>
    )
}

// Duration formatting utility
const formatDuration = (milliseconds: number): string => {
    if (!milliseconds || milliseconds < 0) return '0s'

    const seconds = Math.floor(milliseconds / 1000)
    const minutes = Math.floor(seconds / 60)
    const hours = Math.floor(minutes / 60)
    const days = Math.floor(hours / 24)

    if (days > 0) {
        const remainingHours = hours % 24
        return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`
    } else if (hours > 0) {
        const remainingMinutes = minutes % 60
        return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`
    } else if (minutes > 0) {
        const remainingSeconds = seconds % 60
        return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`
    } else {
        return `${seconds}s`
    }
}

// Custom tooltip formatter for duration values
const CustomTooltip = ({ active, payload, label, isDuration, dateFormatter }: any) => {
    if (active && payload && payload.length) {
        // Format the label if it's a date and we have a date formatter
        let formattedLabel = label
        if (dateFormatter && label) {
            formattedLabel = dateFormatter(label)
        }
        
        return (
            <div className="custom-tooltip" style={{ backgroundColor: 'white', padding: '10px', border: '1px solid #ccc', borderRadius: '4px' }}>
                <p className="label">{`${formattedLabel}`}</p>
                {payload.map((entry: any, index: number) => (
                    <p key={index} style={{ color: entry.color }}>
                        {`${entry.name}: ${isDuration && typeof entry.value === 'number' ? formatDuration(entry.value) : entry.value}`}
                    </p>
                ))}
            </div>
        )
    }
    return null
}

interface AnalyticsLineChartProps {
    title?: string
    data: any[]
    xKey?: string
    dataKey?: string
    series?: { dataKey: string, name: string, color?: string }[]
    category?: string
    transitioning?: boolean
    compact?: boolean
    color?: string
    isDuration?: boolean
    durationScale?: { scale: number, unit: string, unitLabel: string }
    // New date formatting props
    dateFormat?: DateFormatType | string
    autoDetectDateFormat?: boolean
    marginModifier?: number    // Add margin modifier prop
    onPointClick?: (payload: {
        chartType: string
        labelKey: string
        labelValue: string | number
        row: any
    }) => void
}

const lineWidth = 5
export function AnalyticsLineChart(props: AnalyticsLineChartProps) {
    const {
        title,
        data,
        xKey,
        dataKey,
        series,
        category,
        transitioning,
        compact,
        color,
        isDuration = false,
        durationScale,
        dateFormat,
        autoDetectDateFormat = true,
        onPointClick,
    } = props
    const [focusDate, setFocusDate] = useState({ average: null, total: null })
    const [aggregation, setAggregation] = useState(Aggregation.Total)
    const [chartData, setChartData] = useState<{ byDate: Dict[], byHour: Dict[], keys: string[], byCategory?: Dict[] }>(null)
    const [palette, setPalette] = useState(null)
    const [renderLabels, setRenderLabels] = useState(true)

    // Enhanced date formatting configuration
    const dateFormatConfig = useMemo((): DateFormatConfig => {
        // If dateFormat is explicitly provided
        if (dateFormat) {
            if (typeof dateFormat === 'string') {
                // Check if it's a predefined type
                if (['daily', 'monthly', 'yearly'].includes(dateFormat)) {
                    return { type: dateFormat as DateFormatType }
                } else {
                    // Treat as custom moment.js format string
                    return { type: 'custom', format: dateFormat }
                }
            }
        }

        // Auto-detect if enabled and no explicit format provided
        if (autoDetectDateFormat && data && data.length > 0) {
            const detectedType = detectDateGranularity(data, xKey || 'date')
            return { type: detectedType }
        }

        // Default to daily
        return { type: 'daily' }
    }, [dateFormat, autoDetectDateFormat, data, xKey])

    // Create the appropriate date formatter
    const enhancedDateFormatter = useMemo(() => {
        return createDateFormatter(dateFormatConfig)
    }, [dateFormatConfig])

    // Custom Y-axis formatter for duration values
    const yAxisFormatter = (value: number) => {
        if (isDuration && durationScale) {
            // For Y-axis, just show the scaled numeric value with unit indicator
            return `${value}${durationScale.unit}`
        }
        return formatYAxisLabelWithWrap(value)
    }

    // Custom tooltip formatter that shows the full formatted duration
    const customTooltipFormatter = (value: any, name: string, props: any) => {
        if (isDuration && props.payload && props.payload[`${name}_formatted`]) {
            return [props.payload[`${name}_formatted`], name]
        }
        return [value, name]
    }

    // Build a color map from series for consistent coloring
    // MUST be before the early return to maintain hook order
    const seriesColorMap = useMemo(() => {
        const map: Record<string, string> = {}
        if (series) {
            series.forEach(s => {
                if (s.color) map[s.dataKey] = s.color
            })
        }
        return map
    }, [series])

    useEffect(() => {
        let timeout = null
        if (!transitioning) {
            timeout = setTimeout(() => {
                setRenderLabels(true)
            }, 0)
        } else {
            setRenderLabels(false)
        }
        return () => {
            if (timeout) {
                clearTimeout(timeout)
            }
        }

    }, [transitioning])

    useEffect(() => {
        // Determine the keys to use for charting (series keys or single dataKey)
        const seriesKeys = series && series.length > 0
            ? series.map(s => s.dataKey)
            : [dataKey || 'count']

        // If data is already in the correct format (array of objects with xKey and dataKey), just set it directly
        if (data && data.length > 0 && data[0][xKey || 'date'] !== undefined && data[0][seriesKeys[0]] !== undefined) {
            // Fill in missing dates even for pre-formatted data
            const sortedData = [...data].sort((a, b) => 
                Date.parse(a[xKey || 'date']) - Date.parse(b[xKey || 'date'])
            )
            
            // Detect granularity to skip day-gap filling for monthly data
            const granularity = detectDateGranularity(sortedData, xKey || 'date')
            const isMonthlyData = granularity === 'monthly' || dateFormat === 'monthly'

            if (sortedData.length > 1 && !isMonthlyData) {
                const filledData = []
                
                for (let i = 0; i < sortedData.length; i++) {
                    filledData.push(sortedData[i])
                    
                    // Fill in missing dates between current and next
                    if (i < sortedData.length - 1) {
                        const currentDate = moment(sortedData[i][xKey || 'date'])
                        const nextDate = moment(sortedData[i + 1][xKey || 'date'])
                        const daysDiff = nextDate.diff(currentDate, 'days')
                        
                        // Fill in any missing days
                        for (let d = 1; d < daysDiff; d++) {
                            const missingDate = currentDate.clone().add(d, 'days')
                            const missingEntry: any = {
                                [xKey || 'date']: missingDate.format('YYYY-MM-DD'),
                                category: missingDate.format('YYYY-MM-DD')
                            }
                            seriesKeys.forEach(key => { missingEntry[key] = 0 })
                            filledData.push(missingEntry)
                        }
                    }
                }
                
                setChartData({ byDate: filledData, byHour: [], keys: seriesKeys })
            } else {
                const formattedData = sortedData.map(item => ({
                    ...item,
                    category: item[xKey || 'date']
                }))
                setChartData({ byDate: formattedData, byHour: [], keys: seriesKeys })
            }
            return
        }

        let byDate = {}
        let byHour = {}
        let keys = []
        let byCategory = {}

        if (data != null && data.length > 0) {
            // ...existing complex data processing...
        }

        // ...rest of existing data processing logic...

        const dates = Object.keys(byDate)

        // Post process
        switch (aggregation) {
            default:
                break
            case Aggregation.Total: {
                // Convert to array and sort
                let dateArray = Object.keys(byDate)
                    .sort((a, b) => Date.parse(a) - Date.parse(b))
                    .map((y) => {
                        // Fill in missing keys for existing dates
                        for (let i = 0; i < keys.length; i += 1) {
                            if (!(keys[i] in byDate[y])) {
                                byDate[y][keys[i]] = 0
                            }
                        }
                        return byDate[y]
                    })
                
                // Fill in completely missing dates with zero values
                const isMonthlyFill = dateFormatConfig.type === 'monthly' || dateFormat === 'monthly'
                if (dateArray.length > 1 && !isMonthlyFill) {
                    const filledDateArray = []
                    
                    for (let i = 0; i < dateArray.length; i++) {
                        filledDateArray.push(dateArray[i])
                        
                        // Check if we need to fill in dates between current and next
                        if (i < dateArray.length - 1) {
                            const currentDate = moment(dateArray[i].date)
                            const nextDate = moment(dateArray[i + 1].date)
                            const daysDiff = nextDate.diff(currentDate, 'days')
                            
                            // Fill in any missing days with zero values
                            for (let d = 1; d < daysDiff; d++) {
                                const missingDate = currentDate.clone().add(d, 'days')
                                const missingEntry: any = {
                                    date: missingDate.toDate(),
                                    category: missingDate.format('YYYY-MM-DD')
                                }
                                
                                // Add zero for each key
                                keys.forEach(key => {
                                    missingEntry[key] = 0
                                })
                                
                                filledDateArray.push(missingEntry)
                            }
                        }
                    }
                    
                    byDate = filledDateArray
                } else {
                    byDate = dateArray
                }
                break
            }
            case Aggregation.Average:
                // Calculate averages
                byDate = Object.keys(byDate).sort((a, b) => a - b).map((y) => {
                    const ret = byDate[y]
                    dataKeyArray.forEach(key => {
                        if (ret[key] !== undefined && ret.set && ret.set.size > 0) {
                            ret[key] = ret[key] / ret.set.size
                        }
                    })
                    if (ret.set) {
                        delete ret.set
                    }
                    return ret
                })
                for (let i = 0; i < dates.length; i += 1) {
                    if (byHour[dates[i]]) {
                        byHour[dates[i]] = Object.keys(byHour[dates[i]]).sort((a, b) => a - b).map((y) => {
                            const ret = byHour[dates[i]][y]
                            dataKeyArray.forEach(key => {
                                if (ret[key] !== undefined && ret.set && ret.set.size > 0) {
                                    ret[key] = ret[key] / ret.set.size
                                }
                            })
                            if (ret.set) {
                                delete ret.set
                            }
                            return ret
                        })
                    }
                }
                break
        }
        setPalette(distinctColors({
            count: dataKeyArray.length || dates.length
        }))
        setChartData({ byDate, byHour, keys: dataKeyArray, byCategory })
    }, [aggregation, data, xKey, category, dataKey])

    function drillDown(e: { date: Date }) {
        setFocusDate({ ...focusDate, [aggregation]: e.date })
    }
    function drillUp(e) {
        setFocusDate({ ...focusDate, [aggregation]: null })
    }

    function emitPointClick(entry: any) {
        if (!onPointClick) {
            return
        }
        const row = entry?.payload || entry
        const labelKey = xKey || 'category'
        const labelValue = row?.[labelKey] ?? row?.date ?? row?.category
        if (labelValue === undefined || labelValue === null) {
            return
        }
        onPointClick({
            chartType: 'line',
            labelKey,
            labelValue,
            row,
        })
    }

    function handleLineClick(entry: any) {
        emitPointClick(entry)
        if (xKey && focusDate[aggregation] === null && entry?.payload?.date) {
            drillDown(entry.payload)
        }
    }

    function handleChartClick(state: any) {
        const payload = state?.activePayload?.[0]?.payload
        if (!payload) {
            return
        }
        handleLineClick({ payload })
    }

    function changeAggregation(x: Aggregation) {
        setAggregation(x)
    }

    if (chartData == null) {
        return null
    }

    const dataKeyArray = series && series.length > 0
        ? series.map(s => s.dataKey)
        : (chartData?.keys && chartData.keys.length > 0 ? chartData.keys : [dataKey || 'count'])

    if (compact || xKey) {
        const modifier = props.marginModifier || 1.0
        return <div className={`analytics-line-chart${onPointClick ? ' drillable' : ''}`}>
            <ResponsiveContainer width="100%" height="100%">
                <LineChart 
                    data={chartData.byDate} 
                    onClick={handleChartClick}
                    margin={{
                        top: Math.round(20 * modifier),
                        right: Math.round(30 * modifier),
                        bottom: Math.round(20 * modifier),
                        left: Math.round(60 * modifier),
                    }}
                >
                    <CartesianGrid stroke="#888" />
                    <XAxis
                        dataKey={!xKey ? 'category' : xKey}
                        tickFormatter={!xKey ? undefined : (aggregation === Aggregation.Average ? dayFormatter : enhancedDateFormatter)}
                    />
                    <YAxis
                        tickFormatter={yAxisFormatter}
                    />
                    <Tooltip 
                        formatter={customTooltipFormatter}
                        content={<CustomTooltip isDuration={isDuration} dateFormatter={enhancedDateFormatter} />}
                    />
                    {dataKeyArray.map((key, index) => (
                        <Line
                            key={key}
                            type="monotone"
                            dataKey={key}
                            stroke={seriesColorMap[key] || color || getAnalyticsColor(`${title || ''}-${key}`, index)}
                            strokeWidth={lineWidth}
                            dot={renderLabels ? <CustomLabel isDuration={isDuration} dataKey={key} /> : false}
                            onClick={handleLineClick}
                        />
                    ))}
                    <Legend formatter={formatLegendText} />
                </LineChart>
            </ResponsiveContainer>
        </div>
    }

    if (focusDate[aggregation] == null) {
        // Extract var(--secondary-color) from document
        const secondaryColor = getComputedStyle(document.documentElement).getPropertyValue('--secondary-mid')
        const modifier = props.marginModifier || 1.0

        return <div className={`analytics-line-chart${onPointClick ? ' drillable' : ''}`}>
            <ResponsiveContainer width="100%" height="100%" className={xKey ? "drillable" : ""}>
                <LineChart 
                    data={chartData.byDate}
                    onClick={handleChartClick}
                    margin={{
                        top: Math.round(20 * modifier),
                        right: Math.round(30 * modifier),
                        bottom: Math.round(20 * modifier),
                        left: Math.round(60 * modifier),
                    }}
                >
                    <CartesianGrid stroke="#888" />
                    <XAxis
                        dataKey={!xKey ? 'category' : xKey}
                        tickFormatter={!xKey ? undefined : (aggregation === Aggregation.Average ? dayFormatter : enhancedDateFormatter)}
                    />
                    <YAxis
                        tickFormatter={yAxisFormatter}
                    />
                    <Tooltip 
                        formatter={customTooltipFormatter}
                        content={<CustomTooltip isDuration={isDuration} dateFormatter={enhancedDateFormatter} />}
                    />
                    {dataKeyArray.map((key, index) => (
                        <Line
                            key={key}
                            type="monotone"
                            dataKey={key}
                            stroke={seriesColorMap[key] || color || getAnalyticsColor(`${title || ''}-${key}`, index)}
                            strokeWidth={lineWidth}
                            dot={renderLabels ? <CustomLabel isDuration={isDuration} dataKey={key} /> : false}
                            onClick={handleLineClick}
                        />
                    ))}
                    <Legend formatter={formatLegendText} />
                </LineChart>
            </ResponsiveContainer>
        </div>
    }

    // Remove debug log
    const modifier = props.marginModifier || 1.0
    return <div className={`analytics-line-chart${onPointClick ? ' drillable' : ''}`}>
        <div className="top-left">
            <IconButton noBg alt icon="fas fa-chevron-left" onClick={drillUp} />
        </div>
        <ResponsiveContainer width="100%" height="100%">
            <LineChart 
                data={chartData.byHour[focusDate[aggregation]]}
                onClick={handleChartClick}
                margin={{
                    top: Math.round(20 * modifier),
                    right: Math.round(30 * modifier),
                    bottom: Math.round(20 * modifier),
                    left: Math.round(60 * modifier),
                }}
            >
                <CartesianGrid stroke="#000" />
                <XAxis dataKey="hour" tickFormatter={hourFormatter} />
                <YAxis 
                    tickFormatter={formatYAxisLabelWithWrap}
                    width={Math.round(50 * modifier)}
                />
                {chartData.keys.map((key, ix) => (
                    <Line
                        key={key}
                        type="monotone"
                        label={renderLabels ? <CustomLabel /> : false}
                        dataKey={key}
                        name={formatLegendText(key)}
                        stroke={color || getAnalyticsColor(`${title || ''}-${key}`, ix)}
                        onClick={handleLineClick}
                        strokeWidth={lineWidth}
                        isAnimationActive={renderLabels}
                        animationDuration={renderLabels ? 500 : 0}
                    />
                ))}
                <Tooltip />
                <Legend formatter={formatLegendText} />
            </LineChart>
        </ResponsiveContainer>
        {getOptions()}
    </div>
}
